/* ──────────────────────────────────────────────────────────────────────────
   Where an account is in the SOP's lifecycle, and what the poster does next.

     taken onto a phone → warm-up (3 or 7 days) → 3 days of posting with no
     link → day 4: the creator's link goes straight into the bio → posting.

   Each warm-up day and each posting day is ticked off by the poster (Jay,
   2026-10-07), so the step comes from the ticks, not from the calendar.
     If Instagram flags the bio link: link out of the bio, into a Story
     Highlight, and the bio says "Check my highlight". Once the account is
     fine again it gets a new link, at a new slug, back in the bio (Jay,
     2026-10-12), so flagged and in a highlight are two steps, not one.

   Days are YYYY-MM-DD, counted in whole calendar days. Pure, so the page and
   the server read the same step.
   ────────────────────────────────────────────────────────────────────────── */

export type AccountStatus = "active" | "restricted" | "suspended" | "banned";

/** Why an account was banned (Jay, 2026-10-12): a ban that came from a connected account, or one for 3 reasons. */
export type BanReason = "connected" | "three_reasons";
export const BAN_REASON: Record<BanReason, string> = { connected: "Connected ban", three_reasons: "3 reason ban" };

export type LifecycleDates = {
  warmup: number;
  /** Days ticked so far, in order. */
  warmupTicks: number;
  postingTicks: number;
  takenOn: string | null;
  /** On a phone. Imported accounts have no take date, so phone presence decides. */
  onPhone?: boolean;
  /** Brought in off the sheet (migration 0006): a shorter history than Signet tracks. */
  imported?: boolean;
  /** An imported account that already had its link live when it was imported. */
  importedActive?: boolean;
  warmupDoneOn: string | null;
  linkAddedOn: string | null;
  linkFlaggedOn: string | null;
  status: AccountStatus;
  statusOn: string | null;
  /** Nobody is posting to it for now (Jay, 2026-10-12): "Not posting". */
  pausedOn?: string | null;
  banReason?: BanReason | null;
  /** The flagged link was moved into a Story Highlight. */
  highlightOn?: string | null;
};

export type Stage = "unassigned" | "warmup" | "posting" | "add_link" | "linked" | "flagged" | "highlight" | "restricted" | "paused" | "ended";

export type Step = { stage: Stage; title: string; detail: string; due: boolean };

/** Posting days before the link goes in. */
export const POSTING_DAYS = 3;

export const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export function nextStep(a: LifecycleDates, today: string): Step {
  if (a.status === "banned" || a.status === "suspended") {
    const detail = [a.status === "banned" && a.banReason ? BAN_REASON[a.banReason] : null, a.statusOn ? `since ${a.statusOn}` : null].filter(Boolean).join(", ");
    return { stage: "ended", title: a.status === "banned" ? "Banned" : "Suspended", detail: detail ? detail[0]!.toUpperCase() + detail.slice(1) : "", due: false };
  }
  const placed = a.onPhone ?? Boolean(a.takenOn);
  if (!placed) return { stage: "unassigned", title: "Not on a phone yet", detail: "Take it onto a phone of the same account type.", due: false };
  if (a.pausedOn) return { stage: "paused", title: "Not posting", detail: `Since ${a.pausedOn}. Press Warming up & posting to pick it up again.`, due: false };
  // Imported off the sheet with its link live: its stage comes from the sheet, not from dates it never had.
  // Any other imported account walks the same days as a new one. It used to stop at "Posting, no link
  // yet" with nothing to tick, and stayed there after its link went in (found 2026-10-19).
  if (a.imported) {
    if (a.status === "restricted") return restricted(a);
    if (a.importedActive) return linkState(a, { stage: "linked", title: "Active", detail: "Imported from the sheet with its link live. If Instagram flags the link, mark it flagged.", due: false });
  }
  if (a.warmupTicks < a.warmup) {
    const day = a.warmupTicks + 1;
    return { stage: "warmup", title: `Warm-up day ${day} of ${a.warmup}`, detail: `Do day ${day} of the ${a.warmup}-day warm-up SOP, then tick it.`, due: true };
  }
  if (a.postingTicks < POSTING_DAYS) {
    const day = a.postingTicks + 1;
    return { stage: "posting", title: `Posting day ${day} of ${POSTING_DAYS}`, detail: "Post normally, no link yet, then tick it.", due: true };
  }
  if (!a.linkAddedOn) {
    return { stage: "add_link", title: "Add the link to the bio", detail: "Put the creator's direct link straight in the bio. No @ mention, no highlight.", due: true };
  }
  return linkState(a, { stage: "linked", title: "Posting with the link in bio", detail: "If Instagram flags the link, mark it flagged.", due: false });
}

/** Restricted is an old status (only rows from the sheet have it). It says what to do once the account is fine. */
function restricted(a: LifecycleDates): Step {
  return { stage: "restricted", title: "Restricted", detail: `${a.statusOn ? `Since ${a.statusOn}. ` : ""}Once it's fine again, give it a link.`, due: false };
}

/** Once a link is live: restricted (old rows), flagged, in a highlight, or fine. */
function linkState(a: LifecycleDates, fine: Step): Step {
  if (a.status === "restricted") return restricted(a);
  if (a.linkFlaggedOn && !a.highlightOn) {
    return { stage: "flagged", title: "Link flagged: move it to a highlight", detail: "Take the link out of the bio, put it in a Story Highlight, and change the bio to \"Check my highlight\".", due: true };
  }
  if (a.linkFlaggedOn) return { stage: "highlight", title: "Link in a highlight", detail: "Once the account is fine again, give it a new link in the bio.", due: false };
  return fine;
}
